import React, { KeyboardEvent, useContext, useMemo } from "react";
import {
  Button,
  IconButton,
  Icons,
  Text,
  useToggle,
} from "@pushpress/shared-components";
import {
  GetCalendars,
  disconnectFromGoogle,
} from "../../../apis/googleSyncApi";
import { toast } from "react-toastify";
import { defaultToastOptions, extractError } from "../../../utils/misc";
import ConfirmModal from "../../../components/modals/ConfirmModal/ConfirmModal";
import { AppContext } from "../../../context/app";
import { useCalendarSync } from "../CalendarSyncProvider/CalendarSyncProvider";
import styles from "./ConnectedPanel.module.scss";

interface ConnectedPanelProps {
  userUuid: string;
  confirmDisconnectMessage?: string;
  firstName: string;
  calendarData?: GetCalendars;
  className?: string;
  hideReSyncSection?: boolean;
  onSuccess: VoidFunction;
  onEditClick: VoidFunction;
}

const ConnectedPanel = (props: ConnectedPanelProps) => {
  const {
    userUuid,
    firstName,
    confirmDisconnectMessage,
    calendarData,
    className,
    hideReSyncSection,
    onSuccess,
    onEditClick,
  } = props;

  const { client } = useContext(AppContext);

  const {
    connectGoogleAccount,
    isConnectingGoogle,
    syncGoogleCalendar,
    syncingGoogleCalendar,
    googleCalendarSyncRequested,
  } = useCalendarSync();

  const [confirmDisconnectVisible, confirmDisconnectToggle] = useToggle(false);

  const [isDisconnecting, disconnectToggle] = useToggle(false);

  const handleReSync = async () => {
    syncGoogleCalendar(userUuid, { onSuccess });
  };

  const handleConnect = async () => {
    connectGoogleAccount(userUuid);
  };

  const handleConfirmDisconnect = async () => {
    confirmDisconnectToggle.on();
  };

  const handleDisconnect = async () => {
    disconnectToggle.on();
    try {
      const response = await disconnectFromGoogle(userUuid, client.uuid);
      if (response.status === 200) {
        onSuccess();
        confirmDisconnectToggle.off();
        toast.success("Disconnected from Google", defaultToastOptions);
      } else {
        throw new Error("Failed to disconnect from Google");
      }
    } catch (err) {
      toast.error(extractError(err), defaultToastOptions);
    } finally {
      disconnectToggle.off();
    }
  };

  const handleEditKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter") {
      onEditClick();
    }
  };

  const primaryCalendar = useMemo(() => {
    return calendarData?.calendars?.find(
      (calendar) => calendar.id === calendarData.primaryCalendar,
    )?.name;
  }, [calendarData]);

  const calendarsNames = useMemo(() => {
    if (!calendarData?.calendars) {
      return "--";
    }
    const names = calendarData?.calendars
      .filter((calendar) => calendar.visible)
      .map((calendar) => calendar.name)
      .join(", ");
    return `${names}.`;
  }, [calendarData]);

  const primaryCalendarDoesNotExist = useMemo(() => {
    if (!calendarData?.calendars) {
      return false;
    }
    const primaryCalendar = calendarData?.calendars.find(
      (c) => c.id === calendarData.primaryCalendar,
    );
    return !primaryCalendar;
  }, [calendarData]);

  const email = useMemo(() => {
    if (!calendarData?.calendars) {
      return "--";
    }
    // get the primary calendar used to connect the account
    const primaryGoogleCalendar = calendarData?.calendars.find(
      (c) => c.primary,
    );
    return primaryGoogleCalendar?.id || "--";
  }, [calendarData]);

  return (
    <>
      <div className={`${styles.root} ${className}`}>
        <div className={styles.section}>
          <div className={styles.header}>
            <Text
              variant="heading-5"
              color="wolf-800"
              aria-label="Primary calendar"
            >
              {email}
            </Text>
            <IconButton
              icon={Icons.Trash}
              size={20}
              color="#F5222D"
              aria-label="Disconnect account"
              tooltip="Disconnect"
              onClick={handleConfirmDisconnect}
            />
          </div>
        </div>
        {primaryCalendarDoesNotExist ? (
          <div className={styles.section}>
            <div className={styles.alert}>
              <Icons.Alert className={styles.alertIcon} />
              <Text variant="heading-5" color="wolf-800">
                Connection failed
              </Text>
            </div>
            <Text variant="body-lg" color="wolf-600" className="mt-3">
              The Google Calendar sync between ST Fitness and {email} has an
              expired token. It will not block availability or sync classes and
              appointments until you reconnect.
            </Text>
            <Button
              type="button"
              buttonType="primary"
              className="mt-3"
              fullWidth
              size="large"
              text="Reconnect"
              loading={isConnectingGoogle}
              onClick={handleConnect}
            />
          </div>
        ) : (
          <>
            {!hideReSyncSection && (
              <div className={styles.section}>
                <Text variant="heading-5" color="wolf-800" className="mb-2">
                  Class and appointment sync
                </Text>
                <Text variant="body-lg" color="wolf-600" className="mb-2">
                  A calendar was added to {email} called &quot;{primaryCalendar}
                  &quot;. {firstName}&apos;s classes and appointments from
                  PushPress are automatically be added to it.
                </Text>
                <Text variant="body-lg" color="wolf-600" className="mb-4">
                  On the rare occasion things get out of sync, you can manually
                  re-sync using the button below.
                </Text>
                <Button
                  type="button"
                  buttonType="secondary"
                  fullWidth
                  size="large"
                  text={
                    googleCalendarSyncRequested
                      ? "Re-sync initiated"
                      : "Re-sync classes and appointments"
                  }
                  loading={syncingGoogleCalendar}
                  disabled={googleCalendarSyncRequested}
                  onClick={handleReSync}
                />
              </div>
            )}
            <div
              className={styles.section}
              onClick={onEditClick}
              onKeyDown={handleEditKeyDown}
              tabIndex={0}
              role="button"
            >
              <div className={`${styles.header} mb-2`}>
                <Text variant="heading-5" color="wolf-800">
                  Calendar events block availability
                </Text>
                <Icons.CaretRight width={20} height={20} />
              </div>
              <Text variant="body-lg" color="wolf-600" className="mb-2">
                Events on these calendars block {firstName}&apos;s availability
                in PushPress so that they only get booked at times when they are
                available:
              </Text>
              <Text variant="body-lg" color="wolf-600" noWrap>
                {calendarsNames}
              </Text>
            </div>
          </>
        )}
      </div>
      <ConfirmModal
        visible={confirmDisconnectVisible}
        okayLabel="Disconnect"
        title="Are you sure you want to disconnect?"
        message={confirmDisconnectMessage}
        loading={isDisconnecting}
        okayType="destructive"
        onCancel={confirmDisconnectToggle.off}
        onOkay={handleDisconnect}
      />
    </>
  );
};

export default ConnectedPanel;
